import { useMemo, useState } from "react";
import { thumbHashToApproximateAspectRatio, thumbHashToDataURL } from "thumbhash";

interface Props {
  src?: string | null;
  alt?: string;
  thumbHash?: string | null;
  width?: number | null;
  height?: number | null;
}

const decodeThumbHash = (hash: string): { dataUrl: string; aspectRatio: number } | null => {
  try {
    const bytes = Uint8Array.from(atob(hash), c => c.charCodeAt(0));
    return {
      dataUrl: thumbHashToDataURL(bytes),
      aspectRatio: thumbHashToApproximateAspectRatio(bytes),
    };
  } catch {
    return null;
  }
};

const BOX_SHADOW = "0 2px 12px 0 rgba(117, 117, 117, 0.1)";

export const AssignmentImage: React.FC<Props> = ({ src, alt = "", thumbHash, width, height }) => {
  const [loaded, setLoaded] = useState(false);
  const decoded = useMemo(() => (thumbHash ? decodeThumbHash(thumbHash) : null), [thumbHash]);

  // No thumb hash and no src yet — nothing to show
  if (!decoded && !src) return null;

  // No thumb hash — plain image with shadow
  if (!decoded) {
    return (
      <div style={{ boxShadow: BOX_SHADOW, overflow: "hidden" }}>
        <img src={src!} alt={alt} style={{ display: "block", maxWidth: "100%" }} />
      </div>
    );
  }

  const aspectRatio = width && height ? `${width} / ${height}` : String(decoded.aspectRatio);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio,
        borderRadius: 4,
        boxShadow: BOX_SHADOW,
        overflow: "hidden",
      }}
    >
      {/* Thumb hash placeholder — always rendered until real image is loaded */}
      {!loaded && (
        <img
          src={decoded.dataUrl}
          alt=""
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "fill",
          }}
        />
      )}
      {/* Real image — only start loading once src URL is resolved */}
      {!!src && (
        <img
          src={src}
          alt={alt}
          onLoad={() => {
            setLoaded(true);
          }}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      )}
    </div>
  );
};
